import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { SegmentedTabs } from '@/components/ui/tabs';
import { useAggregateLeaderboard } from '@/api/query/useLeaderboard';
import { useUser } from '@/hooks/useUser';
import { useAuth } from '@/contexts/AuthContext';
import type { UserPrizeData } from '@/utils/prize';
import { UserPickemsList, EXPAND_PICKEMS_GRID } from './UserPickemsList';
import { WeeklyBreakdownList } from './WeeklyBreakdownList';
import { LeaderboardTable, type LeaderboardTableEntry } from './LeaderboardTable';

interface LeaderboardSectionProps {
  title?: string;
  className?: string;
}

const LeaderboardSection: React.FC<LeaderboardSectionProps> = ({ className = '' }) => {
  const { t, i18n } = useTranslation();
  const { currentUser } = useUser();
  const { iframeParams } = useAuth();
  const dateLocale = i18n.language?.startsWith('fr') ? 'fr-CA' : 'en-CA';

  const { weeks, latestWeekOffset } = useMemo(() => {
    const SEASON_START_DATE = new Date('2025-10-13T00:00:00Z');
    const today = new Date();
    const daysSinceReference = Math.floor(
      (today.getTime() - SEASON_START_DATE.getTime()) / (1000 * 60 * 60 * 24)
    );
    const currentWeekNumber = Math.floor(daysSinceReference / 7) + 1;

    const weeksArray = Array.from({ length: Math.max(1, currentWeekNumber) }, (_, i) => ({
      label: t('leaderboard.weekLabel', { n: i + 1 }),
      offset: i,
    })).reverse();

    const latestOffset = Math.max(0, currentWeekNumber - 1);

    return { weeks: weeksArray, latestWeekOffset: latestOffset };
  }, [t]);

  const days = useMemo(() => {
    const fmt = new Intl.DateTimeFormat(dateLocale, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date();
      d.setDate(d.getDate() - i);
      return { offset: i, label: i === 0 ? t('leaderboard.today') : fmt.format(d) };
    });
  }, [t, dateLocale]);

  const months = useMemo(() => {
    const fmt = new Intl.DateTimeFormat(dateLocale, { month: 'short', year: 'numeric' });
    return Array.from({ length: 6 }, (_, i) => {
      const d = new Date();
      d.setDate(1);
      d.setMonth(d.getMonth() - i);
      return { offset: i, label: fmt.format(d) };
    });
  }, [dateLocale]);

  const [activeTab, setActiveTab] = useState<'today' | 'week' | 'month' | 'season'>('today');
  const [selectedOffset, setSelectedOffset] = useState(0);

  const period = useMemo(() => {
    if (activeTab === 'season') return 'year';
    if (activeTab === 'month') return 'month';
    if (activeTab === 'week') return 'week';
    return 'today';
  }, [activeTab]);

  const leagueCode = iframeParams.location;

  const { data, isLoading, error, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useAggregateLeaderboard(period, selectedOffset, leagueCode);

  const entries: LeaderboardTableEntry[] = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap(page =>
      page.leaderboard.map(entry => ({
        user_id: entry.user_id,
        username: entry.username,
        points: entry.total_points,
        rank: entry.rank,
        prize: (entry.prize as UserPrizeData | null | undefined) ?? null,
      }))
    );
  }, [data]);

  const tabs = [
    { id: 'today' as const, label: t('leaderboard.daily') },
    { id: 'month' as const, label: t('leaderboard.monthly') },
  ];

  const handleTabChange = (tabId: 'today' | 'week' | 'month' | 'season') => {
    setActiveTab(tabId);
    setSelectedOffset(tabId === 'week' ? latestWeekOffset : 0);
  };

  return (
    <div data-test-id="leaderboard-section" className={`${className}`}>
      <div className="mb-6">
        <SegmentedTabs
          variant="primary"
          value={activeTab}
          onValueChange={value => handleTabChange(value as 'today' | 'week' | 'month' | 'season')}
          aria-label={t('tabs.leaderboard')}
          items={tabs.map(tab => ({
            value: tab.id,
            label: tab.label,
            'data-test-id': `leaderboard-period-${tab.id}`,
          }))}
        />
      </div>

      {activeTab !== 'season' && (
        <div className="mb-7">
          <SegmentedTabs
            listClassName="pb-1"
            variant="pill"
            value={String(selectedOffset)}
            onValueChange={value => setSelectedOffset(Number(value))}
            aria-label={t('leaderboard.daily')}
            items={(activeTab === 'today' ? days : activeTab === 'month' ? months : weeks).map(
              item => ({
                value: String(item.offset),
                label: item.label,
                'data-test-id': `leaderboard-offset-${activeTab}-${item.offset}`,
              })
            )}
          />
        </div>
      )}

      <LeaderboardTable
        className="-mx-6"
        entries={entries}
        currentUserId={currentUser?.id}
        isLoading={isLoading}
        error={!!error}
        hasNextPage={!!hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        onLoadMore={() => fetchNextPage()}
        emptyTitle={t('leaderboard.noData')}
        errorTitle={t('leaderboard.failedToLoad')}
        loadingLabel={t('leaderboard.loading')}
        renderExpanded={(entry, isCurrentUser) => (
          <div
            className={`p-3 border-b border-border ${
              isCurrentUser ? 'bg-white' : 'bg-surface-muted/40'
            }`}
          >
            <div
              className={`${EXPAND_PICKEMS_GRID} text-xs font-sans font-medium pb-2 border-b ${
                isCurrentUser
                  ? 'border-black/15 text-background/70'
                  : 'border-border/20 text-muted-foreground'
              }`}
            >
              <span>
                {activeTab === 'season' ? t('leaderboard.week') : t('leaderboard.pickem')}
              </span>
              <span className="text-center">{t('leaderboard.rank')}</span>
              <span className="text-center">{t('leaderboard.prize')}</span>
              <span className="text-center">{t('leaderboard.points')}</span>
            </div>
            {activeTab === 'season' ? (
              <WeeklyBreakdownList
                userId={entry.user_id}
                leagueCode={leagueCode}
                isCurrentUser={isCurrentUser}
              />
            ) : (
              <UserPickemsList
                userId={entry.user_id}
                period={period}
                weekOffset={selectedOffset}
                leagueCode={leagueCode}
                isCurrentUser={isCurrentUser}
              />
            )}
          </div>
        )}
      />
    </div>
  );
};

export default LeaderboardSection;
